/**
 * V38.08 — Dashboard juriste enrichi.
 *
 * KPIs (revues mois, taux validation, temps moyen), Pipeline (pending/in_progress/etc.),
 * Red Flags (dossiers IA score bas), Deadlines (SLA 10j), Top performers.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Briefcase, TrendingUp, TrendingDown, AlertOctagon, CalendarClock, CheckCircle2, Clock, RefreshCw, Loader2, Scale } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => {
  const t = localStorage.getItem('token')
        || localStorage.getItem('access_token')
        || localStorage.getItem('admin_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export default function JuristeDashboardPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('kpis');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/juriste/dashboard`, { headers: getAuthHeader() });
      setData(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur chargement dashboard juriste');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="juriste-dashboard-loading">
        <Loader2 className="h-6 w-6 animate-spin text-red-600" />
      </div>
    );
  }

  const kpis = data?.kpis || {};
  const pipeline = data?.pipeline || {};
  const redFlags = data?.red_flags || [];
  const deadlines = data?.upcoming_deadlines || [];
  const byJuriste = data?.by_juriste || [];

  const growthIsUp = (kpis.growth_pct || 0) >= 0;

  return (
    <Card data-testid="juriste-dashboard-panel">
      <CardHeader className="border-b">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="flex items-center gap-2 text-red-900">
              <Scale className="h-5 w-5" />
              Dashboard Juriste {data?.scope === 'my_queue' ? '— Ma file' : '— Vue globale'}
            </CardTitle>
            <CardDescription>
              KPIs, pipeline, dossiers à risque et deadlines (SLA {kpis.sla_days || 10}j).
            </CardDescription>
          </div>
          <Button onClick={fetchData} variant="outline" size="sm" data-testid="juriste-dashboard-refresh">
            <RefreshCw className="h-4 w-4 mr-1" /> Actualiser
          </Button>
        </div>
      </CardHeader>

      <CardContent className="pt-4">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="kpis" data-testid="juriste-dashboard-tab-kpis">KPIs</TabsTrigger>
            <TabsTrigger value="pipeline" data-testid="juriste-dashboard-tab-pipeline">Pipeline</TabsTrigger>
            <TabsTrigger value="red-flags" data-testid="juriste-dashboard-tab-red-flags">
              Red Flags ({redFlags.length})
            </TabsTrigger>
            <TabsTrigger value="deadlines" data-testid="juriste-dashboard-tab-deadlines">
              Deadlines ({deadlines.length})
            </TabsTrigger>
            {byJuriste.length > 0 && (
              <TabsTrigger value="performers" data-testid="juriste-dashboard-tab-performers">
                Par juriste ({byJuriste.length})
              </TabsTrigger>
            )}
          </TabsList>

          {/* KPIs */}
          <TabsContent value="kpis" className="pt-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Card>
                <CardContent className="pt-4">
                  <p className="text-xs text-slate-500 flex items-center gap-1"><Briefcase className="h-3 w-3" /> Revues ce mois</p>
                  <p className="text-3xl font-bold text-red-800" data-testid="juriste-kpi-reviews-month">{kpis.reviews_this_month || 0}</p>
                  <p className={`text-xs mt-1 flex items-center gap-1 ${growthIsUp ? 'text-emerald-700' : 'text-red-700'}`}>
                    {growthIsUp ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                    {kpis.growth_pct > 0 ? '+' : ''}{kpis.growth_pct}% vs mois N-1
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <p className="text-xs text-slate-500 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Taux validation</p>
                  <p className="text-3xl font-bold text-emerald-700" data-testid="juriste-kpi-validation-rate">{kpis.validation_rate_pct || 0}%</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <p className="text-xs text-slate-500 flex items-center gap-1"><Clock className="h-3 w-3" /> Temps moyen</p>
                  <p className="text-3xl font-bold text-blue-700" data-testid="juriste-kpi-avg-time">{kpis.avg_processing_time_hours || 0}h</p>
                  <p className="text-[10px] text-slate-400">Assignation → décision</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <p className="text-xs text-slate-500 flex items-center gap-1"><AlertOctagon className="h-3 w-3" /> En retard SLA</p>
                  <p className="text-3xl font-bold text-amber-700" data-testid="juriste-kpi-overdue">{data?.overdue_count || 0}</p>
                  <p className="text-[10px] text-slate-400">+{kpis.sla_days}j sans décision</p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Pipeline */}
          <TabsContent value="pipeline" className="pt-3" data-testid="juriste-dashboard-pipeline">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Card>
                <CardContent className="pt-4">
                  <p className="text-xs text-slate-500">À assigner</p>
                  <p className="text-3xl font-bold text-slate-700">{pipeline.pending || 0}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <p className="text-xs text-slate-500">En cours</p>
                  <p className="text-3xl font-bold text-blue-700">{pipeline.in_progress || 0}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <p className="text-xs text-slate-500">Validées</p>
                  <p className="text-3xl font-bold text-emerald-700">{pipeline.validated || 0}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <p className="text-xs text-slate-500">Rejetées</p>
                  <p className="text-3xl font-bold text-red-700">{pipeline.rejected || 0}</p>
                </CardContent>
              </Card>
            </div>
            <p className="mt-3 text-xs text-slate-500">Total : {pipeline.total || 0} dossiers</p>
          </TabsContent>

          {/* Red Flags */}
          <TabsContent value="red-flags" className="pt-3">
            {redFlags.length === 0 ? (
              <p className="italic text-slate-500">Aucun dossier à risque actuellement.</p>
            ) : (
              <div className="border rounded">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left text-xs">
                    <tr>
                      <th className="px-3 py-2">Référence</th>
                      <th className="px-3 py-2">Client</th>
                      <th className="px-3 py-2">Score IA</th>
                      <th className="px-3 py-2">Red flags</th>
                      <th className="px-3 py-2">Juriste</th>
                      <th className="px-3 py-2">Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {redFlags.map((r) => (
                      <tr key={r.document_id} className="border-t" data-testid={`juriste-red-flag-row-${r.document_id}`}>
                        <td className="px-3 py-2 font-mono text-xs">{r.reference || '—'}</td>
                        <td className="px-3 py-2">{r.client_name || '—'}</td>
                        <td className="px-3 py-2">
                          <Badge className={`${(r.ai_score || 100) < 40 ? 'bg-red-600' : 'bg-amber-600'} text-white`}>
                            {r.ai_score ?? '—'}
                          </Badge>
                        </td>
                        <td className="px-3 py-2 text-red-700 font-semibold">{r.ai_red_flags_count}</td>
                        <td className="px-3 py-2 text-xs">{r.assigned_to || '—'}</td>
                        <td className="px-3 py-2"><Badge variant="outline">{r.status}</Badge></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          {/* Deadlines */}
          <TabsContent value="deadlines" className="pt-3">
            {deadlines.length === 0 ? (
              <p className="italic text-slate-500">Aucun dossier en file d'attente.</p>
            ) : (
              <div className="border rounded">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left text-xs">
                    <tr>
                      <th className="px-3 py-2">Référence</th>
                      <th className="px-3 py-2">Client</th>
                      <th className="px-3 py-2">Juriste</th>
                      <th className="px-3 py-2">Deadline</th>
                      <th className="px-3 py-2">Jours restants</th>
                      <th className="px-3 py-2">Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deadlines.map((d) => (
                      <tr key={d.document_id} className={`border-t ${d.overdue ? 'bg-red-50' : ''}`} data-testid={`juriste-deadline-row-${d.document_id}`}>
                        <td className="px-3 py-2 font-mono text-xs">{d.reference || '—'}</td>
                        <td className="px-3 py-2">{d.client_name || '—'}</td>
                        <td className="px-3 py-2 text-xs">{d.assigned_to || '—'}</td>
                        <td className="px-3 py-2 text-xs">{d.deadline ? new Date(d.deadline).toLocaleDateString('fr-FR') : '—'}</td>
                        <td className="px-3 py-2">
                          {d.overdue ? (
                            <Badge className="bg-red-600 text-white font-semibold"><AlertOctagon className="h-3 w-3 mr-0.5" /> {Math.abs(d.days_remaining)}j de retard</Badge>
                          ) : (
                            <Badge variant="outline" className={d.days_remaining < 3 ? 'text-amber-700 border-amber-300' : 'text-emerald-700 border-emerald-300'}>
                              <CalendarClock className="h-3 w-3 mr-0.5" /> {d.days_remaining}j
                            </Badge>
                          )}
                        </td>
                        <td className="px-3 py-2"><Badge variant="outline">{d.status}</Badge></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          {/* Par juriste */}
          {byJuriste.length > 0 && (
            <TabsContent value="performers" className="pt-3">
              <div className="border rounded">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left text-xs">
                    <tr>
                      <th className="px-3 py-2">Juriste</th>
                      <th className="px-3 py-2 text-center">À assigner</th>
                      <th className="px-3 py-2 text-center">En cours</th>
                      <th className="px-3 py-2 text-center">Validées</th>
                      <th className="px-3 py-2 text-center">Rejetées</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byJuriste.map((j) => (
                      <tr key={j.juriste} className="border-t" data-testid={`juriste-performer-row-${j.juriste}`}>
                        <td className="px-3 py-2 font-mono text-xs">{j.juriste || '—'}</td>
                        <td className="px-3 py-2 text-center">{j.pending || 0}</td>
                        <td className="px-3 py-2 text-center text-blue-700">{j.in_progress || 0}</td>
                        <td className="px-3 py-2 text-center text-emerald-700">{j.validated || 0}</td>
                        <td className="px-3 py-2 text-center text-red-700">{j.rejected || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabsContent>
          )}
        </Tabs>
      </CardContent>
    </Card>
  );
}
